import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/dateFormat";
import { CheckCircle2, XCircle } from "lucide-react";

function fillTemplate(tpl, values) {
  let out = tpl || "";
  Object.entries(values).forEach(([k, v]) => {
    const sv = String(v ?? "");
    out = out.split(`{{${k}}}`).join(sv).split(`{${k}}`).join(sv);
  });
  return out;
}

export default function ApprovalDialog({
  open,
  booking,
  bulkCount = 0,
  bulkRecipients = [], // [{id, user_name, date, volume_m3}]
  action = "approve", // "approve" | "reject"
  municipality,
  onCancel,
  onConfirm,
}) {
  const { t, lang } = useI18n();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const isBulk = bulkCount > 1;
  const isReject = action === "reject";

  useEffect(() => {
    if (!open) return;
    if (isReject) {
      // Reject dialog: start with an empty custom message (backend uses the
      // standard rejection HTML when message is blank).
      setMessage("");
      return;
    }
    const tpl =
      lang === "en"
        ? municipality?.approval_message_en
        : municipality?.approval_message_de;
    if (isBulk) {
      setMessage(tpl || "");
    } else if (booking) {
      const filled = fillTemplate(tpl || "", {
        name: booking.user_name || "",
        date: booking.date,
        volume: booking.volume_m3,
        municipality: municipality?.name || "",
      });
      setMessage(filled);
    }
  }, [open, booking, municipality, lang, isBulk, isReject]);

  const resetToTemplate = () => {
    if (isReject) {
      setMessage("");
      return;
    }
    const tpl =
      lang === "en"
        ? municipality?.approval_message_en
        : municipality?.approval_message_de;
    if (isBulk) {
      setMessage(tpl || "");
    } else if (booking) {
      setMessage(
        fillTemplate(tpl || "", {
          name: booking.user_name || "",
          date: booking.date,
          volume: booking.volume_m3,
          municipality: municipality?.name || "",
        }),
      );
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(message.trim());
    } finally {
      setBusy(false);
    }
  };

  const title = isReject
    ? isBulk
      ? t("bulk_dialog_title_reject")
      : t("reject")
    : isBulk
      ? t("bulk_dialog_title_approve")
      : t("approve_dialog_title");

  const confirmLabel = isReject ? t("reject_send") : t("approve_send");
  const ConfirmIcon = isReject ? XCircle : CheckCircle2;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-border p-3 text-sm">
            <div className="label-caps mb-1">{t("bookings")}</div>
            <div className="font-mono-jb" data-testid="approval-summary">
              {isBulk
                ? `${bulkCount} ${t("bulk_approve_count")}`
                : booking
                  ? `${booking.user_name} · ${formatDate(booking.date)} · ${booking.volume_m3} m³`
                  : ""}
            </div>
          </div>

          {isBulk && bulkRecipients?.length > 0 && (
            <div
              className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm"
              data-testid="approval-recipients"
            >
              <div className="label-caps mb-2">
                {t("recipients_preview").replace("{count}", String(bulkCount))}
              </div>
              <ul className="max-h-40 space-y-1 overflow-auto text-xs">
                {bulkRecipients.slice(0, 40).map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-3"
                    data-testid={`approval-recipient-${r.id}`}
                  >
                    <span className="truncate">{r.user_name || "—"}</span>
                    <span className="font-mono-jb text-muted-foreground">
                      {formatDate(r.date)} · {r.volume_m3} m³
                    </span>
                  </li>
                ))}
                {bulkRecipients.length > 40 && (
                  <li className="text-muted-foreground">
                    +{bulkRecipients.length - 40}
                  </li>
                )}
              </ul>
            </div>
          )}

          <div>
            <Label>{isReject ? t("approval_message") : t("approval_message")}</Label>
            <Textarea
              rows={9}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="approval-message-input"
            />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>
                {t("placeholders_hint")}
                {isBulk && <> · {t("bulk_placeholder_hint_ext")}</>}
              </span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={resetToTemplate}
                data-testid="approval-reset-btn"
              >
                {t("reset_default")}
              </button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="approval-cancel-btn">
            {t("cancel")}
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || (!isReject && !message.trim())}
            variant={isReject ? "destructive" : "default"}
            className="gap-2"
            data-testid="approval-confirm-btn"
          >
            <ConfirmIcon className="h-4 w-4" />
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
